import { useCallback, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Play, ChevronDown, ChevronRight, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { IssueList } from "@/components/integrity/IssueList";

const API = "/api";
const peso = (n) => `₱${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function monthRange() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return [`${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`, `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(last)}`];
}

function DiffTable({ diffs, tid }) {
  if (!diffs.length) return <p className="text-sm text-slate-500">Daily class counts match the tagged classes.</p>;
  return (
    <table className="w-full text-sm" data-testid={`payroll-diffs-${tid}`}>
      <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Date</th><th>Category</th><th>In payroll</th><th>Tagged classes</th><th>Supporting classes</th></tr></thead>
      <tbody>
        {diffs.map((d, i) => (
          <tr key={i} className="border-t border-slate-100 align-top">
            <td className="py-1.5">{d.date}</td>
            <td>{d.label}</td>
            <td className="font-semibold">{d.recorded}</td>
            <td className="font-semibold">{d.expected}</td>
            <td className="text-xs text-slate-500">
              {d.manual_only_date ? <span className="text-amber-700">No tagged classes this day — possibly a manual entry (kept unless you include manual dates)</span>
                : d.classes.map((c) => `${c.time} ${c.student_name} (${c.status})`).join(", ") || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TeacherRow({ t, onApply, busy }) {
  const [open, setOpen] = useState(false);
  const [includeManual, setIncludeManual] = useState(false);
  const diff = t.class_difference;
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`payroll-row-${t.teacher_id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 min-w-[180px] text-left" data-testid={`payroll-row-toggle-${t.teacher_id}`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="font-medium text-slate-900">{t.teacher_name}</span>
          <span className="text-xs text-slate-400">{t.issues.length} finding(s), {t.date_differences.length} day difference(s)</span>
        </button>
        <div className="text-sm w-36">In payroll: <strong data-testid={`payroll-recorded-${t.teacher_id}`}>{t.recorded_classes}</strong> · {peso(t.recorded_earnings)}</div>
        <div className="text-sm w-36">Tagged: <strong data-testid={`payroll-expected-${t.teacher_id}`}>{t.expected_classes}</strong> · {peso(t.expected_earnings)}</div>
        <div className={`text-sm font-semibold w-28 ${diff === 0 ? "text-slate-400" : "text-rose-700"}`}>{diff === 0 ? "Totals match" : diff > 0 ? `${diff} over-counted` : `${-diff} under-counted`}</div>
        {t.needs_correction && t.linked ? (
          <Button size="sm" disabled={busy} onClick={() => onApply(t, includeManual)} data-testid={`payroll-apply-${t.teacher_id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply correction
          </Button>
        ) : <span className="text-xs text-slate-400 w-32 text-right">Review only</span>}
      </div>
      {open && (
        <div className="px-10 pb-4 space-y-3">
          {t.paid_periods.length > 0 && (
            <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Overlaps saved payroll: {t.paid_periods.map((p) => `${p.period_start} → ${p.period_end}`).join(", ")}</p>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Checkbox checked={includeManual} onCheckedChange={(v) => setIncludeManual(!!v)} data-testid={`payroll-include-manual-${t.teacher_id}`} />
            Also reset days that have no tagged classes (manual entries)
          </label>
          <DiffTable diffs={t.date_differences} tid={t.teacher_id} />
          <IssueList issues={t.issues} testid={`payroll-issues-${t.teacher_id}`} />
        </div>
      )}
    </div>
  );
}

export function PayrollAuditPanel() {
  const { getAuthHeader } = useAuth();
  const [[start, end], setRange] = useState(monthRange());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/integrity/payroll`, { params: { start_date: start, end_date: end }, headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  }, [start, end, getAuthHeader]);

  const apply = async (t, includeManual) => {
    const paid = t.paid_periods.length > 0;
    const msg = `Apply payroll correction for ${t.teacher_name} (${data.period_start} → ${data.period_end})?\n\nClasses: ${t.recorded_classes} → ${t.expected_classes}\nEarnings: ${peso(t.recorded_earnings)} → ${peso(t.expected_earnings)}` +
      (paid ? "\n\nWARNING: this period overlaps a SAVED (paid) payroll." : "") + "\n\nThe change will be logged with your name.";
    if (!window.confirm(msg)) return;
    setBusyId(t.teacher_id);
    try {
      const res = await axios.post(`${API}/admin/integrity/payroll/${t.teacher_id}/apply`, {
        start_date: data.period_start, end_date: data.period_end, expected_total_classes: t.expected_classes,
        acknowledge_paid_period: paid, include_manual_only_dates: includeManual,
      }, { headers: getAuthHeader() });
      toast.success(`${t.teacher_name}: ${res.data.correction.original_value} → ${res.data.correction.corrected_value} classes`);
      await run();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Correction failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 mt-4" data-testid="payroll-audit-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Cut-off start</Label><Input type="date" value={start} onChange={(e) => setRange([e.target.value, end])} data-testid="payroll-start-input" /></div>
        <div><Label className="text-xs">Cut-off end</Label><Input type="date" value={end} onChange={(e) => setRange([start, e.target.value])} data-testid="payroll-end-input" /></div>
        <Button onClick={run} disabled={loading || !start || !end} data-testid="run-payroll-audit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run payroll audit
        </Button>
      </div>
      <p className="text-xs text-slate-500">Compares each tutor's payroll counts with the classes actually tagged Present / No Show in the schedule (cancelled classes are never paid).</p>
      {data && (
        <Card><CardContent className="p-0">
          <div className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-50 border-b" data-testid="payroll-summary">
            {data.scanned_teachers} tutors scanned · {data.teachers_with_issues} with findings
          </div>
          {data.teachers.map((t) => <TeacherRow key={t.teacher_id} t={t} onApply={apply} busy={busyId === t.teacher_id} />)}
        </CardContent></Card>
      )}
    </div>
  );
}
